import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Share } from 'lucide-react';
import type { Campaign } from '../../lib/supabase';

type Props = {
  campaign: Campaign;
  onClose: () => void;
};

const QRCodeModal: React.FC<Props> = ({ campaign, onClose }) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const entryUrl = `/entry/${campaign.id}`;

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `${campaign.name.toLowerCase().replace(/\s+/g, '-')}-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const shareQR = async () => {
    const shareUrl = `${window.location.origin}${entryUrl}`;
    
    try {
      await navigator.share({
        title: `Join ${campaign.name}`,
        text: `Scan this QR code to enter the raffle for ${campaign.name}`,
        url: shareUrl,
      });
    } catch (error) {
      // If Web Share API is not supported, copy to clipboard instead
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Raffle QR Code</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-gray-50 p-8 rounded-lg" ref={qrRef}>
            <QRCodeSVG
              value={`${window.location.origin}${entryUrl}`}
              size={250}
              level="H"
              className="mx-auto"
              includeMargin
            />
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Share this QR code to allow participants to easily enter your raffle
            </p>
            <p className="text-xs text-gray-500 break-all">
              {window.location.origin}{entryUrl}
            </p>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={downloadQR}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              <Download className="h-5 w-5 mr-2" />
              Download
            </button>
            <button
              onClick={shareQR}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Share className="h-5 w-5 mr-2" />
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;